<template>
  <div class="ui-document-link" @click.stop>
    <UIButton
      v-if="isFile"
      :leading="ArrowDownTrayIcon"
      :loading
      :size
      :variant
      :fill
      label="Open Source File"
      @click="handleClickOpen"
    />
    <a
      v-else-if="isClickablePath"
      :href="path"
      target="_blank"
    >
      <UIButton
        :leading="LinkIcon"
        :size
        :variant
        :fill
      >
        Open Link
      </UIButton>
    </a>
    <div v-else class="ui-document-link__text">
      {{ displayValue }}
    </div>
    <UIButton
      v-if="editable"
      :leading="PencilIcon"
      :size
      variant="secondary"
      fill="filled"
      @click="handleClickEdit"
    />
  </div>
</template>

<script setup lang="ts">
import { computed, ref } from 'vue'

import { ButtonFill, ButtonSize, ButtonVariant, DocumentSource } from '@types'

import { isPathClickable, getDocumentUrl } from './utils/helpers'
import { handleExternalLink } from '@/helpers/common'
import { useNotifications } from '@/plugins/notification'

import { ArrowDownTrayIcon, LinkIcon, PencilIcon } from '@heroicons/vue/24/outline'
import { UIButton } from '@ui/buttons'

type Props = {
  documentId: string
  name?: string
  path?: string
  source?: DocumentSource
  size?: ButtonSize
  variant?: ButtonVariant
  fill?: ButtonFill
  editable?: boolean
}

type Emits = {
  edit: []
}

const { size = 'xs', variant = 'secondary', fill = 'light', ...props } = defineProps<Props>()

const emit = defineEmits<Emits>()

const { error } = useNotifications()

const loading = ref(false)
const internalUrl = ref<string>()

const isClickablePath = computed(() => isPathClickable(props.path))

const displayValue = computed(() => props.path)

const isFile = computed(() => props.source && [DocumentSource.ALLPOSIT, DocumentSource.ALLPOSIT_THUMBNAIL].includes(props.source))

const handleClickEdit = () => {
  emit('edit')
}

const handleClickOpen = async () => {
  if (internalUrl.value) {
    handleExternalLink(internalUrl.value)
    return
  }
  loading.value = true
  try {
    const url = await getDocumentUrl(props.documentId)
    internalUrl.value = url
    handleExternalLink(url)
  } catch (e) {
    error({
      message: 'Failed to open document',
      description: (e as any).message,
    })
  } finally {
    loading.value = false
  }
}
</script>

<style>
.ui-document-link {
  @apply flex gap-2;

  a .ui-button,
  > *:first-child {
    @apply w-full;
    @apply flex-auto;
  }

  &__text {
    @apply flex items-center;
    @apply px-2.5;
    @apply bg-gray-50 dark:bg-gray-800;
    @apply text-base sm:text-sm;
    @apply font-medium;
    @apply rounded-md;
    @apply truncate;
  }
}
</style>
